/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { property, query } from 'lit/decorators.js';
import CDSHeaderNavItem from '@carbon/web-components/es/components/ui-shell/header-nav-item.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import styles from './masthead.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { stablePrefix: c4dPrefix } = settings;

/**
 * Masthead top nav item.
 *
 * @element c4d-top-nav-item
 */
@customElement(`${c4dPrefix}-top-nav-item`)
class C4DTopNavItem extends CDSHeaderNavItem {
  /**
   * The `<a>`.
   */
  @query('[part="link"]')
  private _linkNode?: HTMLAnchorElement;

  /**
   * `true` if this nav item should be in its active state.
   */
  @property({ type: Boolean, reflect: true })
  active = false;

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('active')) {
      const { active, _linkNode: linkNode } = this;
      if (linkNode) {
        linkNode.setAttribute('data-selected', String(Boolean(active)));
      }
    }
  }

  firstUpdated() {
    this.shadowRoot
      ?.querySelectorAll('[role="menuitem"]')
      .forEach((menuItem) => menuItem.removeAttribute('role'));

    if (this.hasAttribute('role') && this.getAttribute('role') === 'listitem') {
      this.removeAttribute('role');
    }
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

export default C4DTopNavItem;
